<template>
  <div class="pa-3">
    <!-- TOP BAR: Search + Add Landlord -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="8">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by Reference or Display Name"
          outlined dense clearable
          class="rounded-lg small-text-field"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <v-btn
          @click="$router.push('/AddLandlords')"
          small class="save-btn px-6"
          style="border-radius: 20px; font-weight: bold; font-size: 0.875rem;"
        >
          <v-icon left small>mdi-plus</v-icon> Add Landlord
        </v-btn>
      </v-col>
    </v-row>

    <!-- FULL WIDTH LANDLORDS TABLE -->
    <v-data-table
      :headers="headers"
      :items="landlords"
      :search="search"
      :items-per-page="15"
      class="elevation-2 compact-table"
      style="border-radius: 12px; overflow: hidden;"
      :expanded.sync="expanded"
      show-expand
      dense
    >
      <!-- Status Chip -->
      <template v-slot:item.isActive="{ item }">
        <v-chip color="green" dark small>
          Active
        </v-chip>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center" style="gap: 4px;">
          <v-btn icon small @click="goToInvoice(item)" class="action-btn">
            <v-icon small color="blue darken-2">mdi-receipt-text</v-icon>
          </v-btn>
          <v-btn icon small @click="editLandlord(item)" class="action-btn">
            <v-icon small color="grey darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="confirmDelete(item)" class="action-btn">
            <v-icon small color="red">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- EXPANDED ROW -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-3" style="background: #f8f9fa;">
          <v-row dense>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Reference:</strong>
              <span class="black--text">{{ item.reference }}</span>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Display Name:</strong>
              <span class="black--text">{{ item.displayName }}</span>
            </v-col>
            <v-col cols="12" md="4" class="py-1">
              <strong class="black--text">Email:</strong>
              <span class="black--text">{{ item.email || '—' }}</span>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToAttachments(item)">
                <v-icon left small>mdi-folder-multiple</v-icon> Attachments
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToNotes(item)">
                <v-icon left small>mdi-note-text</v-icon> Notes
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToReports(item)">
                <v-icon left small>mdi-chart-bar</v-icon> Reports
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToInvoice(item)">
                <v-icon left small>mdi-receipt-text</v-icon> Invoice
              </v-btn>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>

    <!-- DELETE CONFIRM DIALOG -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card class="pa-3">
        <v-card-title class="red--text text-subtitle-1 font-weight-bold">Delete Landlord?</v-card-title>
        <v-card-text class="black--text">
          Are you sure you want to delete landlord:<br>
          <strong>{{ landlordToDelete?.displayName }} ({{ landlordToDelete?.reference }})</strong>?<br><br>
          This action <strong>cannot be undone</strong>.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark small @click="deleteLandlord">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";
import { logEvent } from "@/utils/logEvent";

const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      search: "",
      expanded: [],
      deleteDialog: false,
      landlordToDelete: null,
      landlords: [],
      headers: [
        { text: "Reference", value: "reference", width: "120px" },
        { text: "Display Name", value: "displayName" },
        { text: "Status", value: "isActive", align: "center", width: "110px" },
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "140px" },
      ],
    };
  },

  async mounted() {
    await this.fetchLandlords();
    await logEvent("Landlords List Viewed", "", "Landlord Management");
  },

  methods: {
    async fetchLandlords() {
      try {
        const snap = await getDocs(collection(db, "landlords"));
        const landlords = snap.docs.map(doc => ({
          id: doc.id,
          reference: doc.data().reference || "N/A",
          displayName: doc.data().displayAs || "Unnamed Landlord",
          email: doc.data().email || "",
          isActive: true,
        }));

        landlords.sort((a, b) => a.reference.localeCompare(b.reference));
        this.landlords = landlords;
      } catch (e) {
        console.error("Error loading landlords:", e);
      }
    },

    editLandlord(item) {
      logEvent("Landlord Edit Opened", `${item.displayName} (${item.reference})`, "Landlord Management");
      this.$router.push(`/EditLandlords/${item.id}`);
    },

    goToAttachments(item) {
      logEvent("Landlord Attachments Viewed", `${item.displayName} (${item.reference})`, "Landlord Management");
      this.$router.push({ name: "Attachments", params: { collection: "landlords", id: item.id } });
    },

    goToNotes(item) {
      logEvent("Landlord Notes Viewed", `${item.displayName} (${item.reference})`, "Landlord Management");
      this.$router.push({ name: "Notes", params: { id: item.id }, query: { collection: "landlords" } });
    },

    goToReports(item) {
      logEvent("Landlord Reports Viewed", `${item.displayName} (${item.reference})`, "Landlord Management");
      this.$router.push({ name: "Reports", params: { id: item.id }, query: { collection: "landlords" } });
    },

    goToInvoice(item) {
      logEvent("Landlord Invoice Viewed", `${item.displayName} (${item.reference})`, "Landlord Management");
      this.$router.push({ name: "LandlordInvoice", params: { id: item.id } });
    },

    confirmDelete(item) {
      this.landlordToDelete = item;
      this.deleteDialog = true;
      logEvent("Landlord Delete Initiated", `${item.displayName} (${item.reference})`, "Landlord Management");
    },

    async deleteLandlord() {
      if (!this.landlordToDelete) return;

      try {
        await deleteDoc(doc(db, "landlords", this.landlordToDelete.id));
        this.landlords = this.landlords.filter(l => l.id !== this.landlordToDelete.id);

        await logEvent(
          "Landlord Deleted",
          `${this.landlordToDelete.displayName} (${this.landlordToDelete.reference})`,
          "Landlord Management"
        );

        alert("Landlord deleted successfully!");
      } catch (e) {
        console.error("Delete failed:", e);
        alert("Failed to delete landlord");
      } finally {
        this.deleteDialog = false;
        this.landlordToDelete = null;
      }
    },
  },
};
</script>

<style scoped>
.compact-table :deep(.v-data-table-header th) {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 10px 12px !important;
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.875rem !important;
  padding: 8px 12px !important;
  color: #000000 !important;
}

.compact-table :deep(tbody td .v-chip) {
  font-size: 0.75rem !important;
  height: 24px !important;
}

.compact-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.compact-table :deep(.v-data-table__expanded__content) {
  font-size: 0.875rem !important;
}

.small-text-field :deep(.v-input__slot) {
  min-height: 40px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.black--text {
  color: #000000 !important;
}

.golden-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  text-transform: none !important;
  font-size: 0.8rem !important;
  min-height: 34px !important;
}

.golden-btn .v-icon {
  color: #000000 !important;
}

.save-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  font-size: 0.875rem !important;
  min-height: 36px !important;
}

.save-btn .v-icon {
  color: #000000 !important;
}

.action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
}

.action-btn .v-icon {
  font-size: 18px !important;
}

.v-dialog .v-card-title {
  font-size: 1rem !important;
  color: #000000 !important;
}

.v-dialog .v-card-text {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.v-dialog .v-btn {
  font-size: 0.8rem !important;
}

@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.75rem !important;
    padding: 8px 8px !important;
  }

  .compact-table :deep(tbody td) {
    font-size: 0.8rem !important;
    padding: 6px 8px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>